import { useCallback, useEffect, useState } from "react";
import { EntityDocumentsSection } from "../../components/EntityDocumentsSection";
import { Modal } from "../../components/Modal";
import { AgingBadge, RiskBadge, StatusBadge } from "../../contasReceber/badges";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import {
  getClientAccount,
  migrateInvoiceToAccount,
  saveClientAccount,
  type ClientAccount,
  type ClientAccountPayload,
  type ClientInvoice,
} from "../../contasReceber/contasReceberApi";
import { getCreditControlSettings } from "../../orcamentos/orcamentosApi";

interface ContaCorrenteSectionProps {
  clientId: number;
  clientName: string;
}

const FREQUENCY_OPTIONS = ["Mensal", "Trimestral", "Semestral", "Anual"];

const parseNumber = (value: string): number | null => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const inputClass =
  "px-3 py-2 border border-border rounded bg-background text-foreground text-sm";

interface AgreementForm {
  installmentAmount: string;
  frequency: string;
  agreementStartDate: string;
  agreementEndDate: string;
  agreementNotes: string;
  hasLegalProcess: boolean;
  legalProcessNotes: string;
  creditLimit: string;
}

const formFromAccount = (account: ClientAccount): AgreementForm => ({
  installmentAmount:
    account.installmentAmount != null ? String(account.installmentAmount) : "",
  frequency: account.frequency ?? "",
  agreementStartDate: account.agreementStartDate
    ? account.agreementStartDate.slice(0, 10)
    : "",
  agreementEndDate: account.agreementEndDate
    ? account.agreementEndDate.slice(0, 10)
    : "",
  agreementNotes: account.agreementNotes ?? "",
  hasLegalProcess: account.hasLegalProcess,
  legalProcessNotes: account.legalProcessNotes ?? "",
  creditLimit: account.creditLimit != null ? String(account.creditLimit) : "",
});

export function ContaCorrenteSection({
  clientId,
  clientName,
}: ContaCorrenteSectionProps) {
  const [account, setAccount] = useState<ClientAccount | null>(null);
  const [form, setForm] = useState<AgreementForm | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  // [3.6.6] Dias mínimos de atraso para a ação "Migrar (JAL)" aparecer (server re-valida).
  const [jalOverdueDays, setJalOverdueDays] = useState(60);
  const [migratingId, setMigratingId] = useState<number | null>(null);
  // Modal de migração JAL (substitui o window.confirm; captura as condições/notas).
  const [migrateInvoice, setMigrateInvoice] = useState<ClientInvoice | null>(null);
  const [migrateNotes, setMigrateNotes] = useState("");
  const [migrateError, setMigrateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getClientAccount(clientId);
      setAccount(result);
      setForm(formFromAccount(result));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível carregar a conta corrente.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  // Limiar JAL (só para mostrar/ocultar a ação de migração; o backend re-valida).
  useEffect(() => {
    let isMounted = true;
    getCreditControlSettings()
      .then((s) => {
        if (isMounted && Number.isFinite(s.jalOverdueDays)) {
          setJalOverdueDays(s.jalOverdueDays);
        }
      })
      .catch(() => {
        // Mantém o default 60 se falhar.
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Fatura elegível para migração JAL: emitida/parcialmente paga, em aberto, vencida há ≥ JAL dias.
  const isJalEligible = (inv: ClientInvoice): boolean =>
    inv.kind === "invoice" &&
    (inv.statusCode === "emitida" || inv.statusCode === "parcialmente_paga") &&
    inv.openAmount > 0 &&
    !inv.isMigratedToAccount &&
    inv.ageDays != null &&
    inv.ageDays >= jalOverdueDays;

  const openMigrate = (inv: ClientInvoice) => {
    setMigrateInvoice(inv);
    setMigrateNotes("");
    setMigrateError(null);
  };

  const confirmMigrate = async () => {
    if (!migrateInvoice || migratingId !== null) return;
    setMigratingId(migrateInvoice.id);
    setMigrateError(null);
    setSavedMessage(null);
    try {
      await migrateInvoiceToAccount(migrateInvoice.id, {
        migrationNotes: migrateNotes.trim() || null,
      });
      setMigrateInvoice(null);
      await load();
      setSavedMessage("Fatura migrada para a conta corrente.");
    } catch (err) {
      setMigrateError(
        err instanceof Error ? err.message : "Não foi possível migrar a fatura.",
      );
    } finally {
      setMigratingId(null);
    }
  };

  const updateForm = (patch: Partial<AgreementForm>) =>
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));

  const handleSave = async () => {
    if (!form || isSaving) return;
    setIsSaving(true);
    setError(null);
    setSavedMessage(null);
    const payload: ClientAccountPayload = {
      installmentAmount: parseNumber(form.installmentAmount),
      frequency: form.frequency.trim() || null,
      agreementStartDate: form.agreementStartDate || null,
      agreementEndDate: form.agreementEndDate || null,
      agreementNotes: form.agreementNotes.trim() || null,
      hasLegalProcess: form.hasLegalProcess,
      legalProcessNotes: form.legalProcessNotes.trim() || null,
      creditLimit: parseNumber(form.creditLimit),
    };
    try {
      const updated = await saveClientAccount(clientId, payload);
      setAccount(updated);
      setForm(formFromAccount(updated));
      setSavedMessage("Conta corrente atualizada.");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível guardar a conta corrente.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const balance = account?.outstandingBalance ?? 0;
  const balancePositive = balance > 0.005;

  const cardStyle = { borderRadius: "var(--radius-card)" } as const;

  return (
    <div className="space-y-6">
      {/* Saldo em dívida */}
      <div className="bg-card border border-border p-6" style={cardStyle}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p
              className="text-secondary-text"
              style={{ fontSize: "var(--text-caption)" }}
            >
              Saldo em dívida
            </p>
            <p
              className={balancePositive ? "text-red-700" : "text-green-700"}
              style={{
                fontSize: "var(--text-h2)",
                fontWeight: "var(--font-weight-bold)",
                lineHeight: "1.2",
              }}
            >
              {isLoading ? "—" : formatCurrency(balance)}
            </p>
            {account && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <RiskBadge
                  level={account.riskLevel}
                  title={
                    account.riskReasons && account.riskReasons.length > 0
                      ? account.riskReasons.join("; ")
                      : undefined
                  }
                />
                {account.riskReasons && account.riskReasons.length > 0 && (
                  <span
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    {account.riskReasons.join(" · ")}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="text-left sm:text-right">
            <p
              className="text-secondary-text"
              style={{ fontSize: "var(--text-caption)" }}
            >
              Faturas em aberto
            </p>
            <p style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
              {account?.openInvoiceCount ?? 0}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      {/* Faturas em aberto */}
      <div className="bg-card border border-border p-6 space-y-4" style={cardStyle}>
        <h4
          className="text-foreground"
          style={{
            fontSize: "var(--text-section-title)",
            fontWeight: "var(--font-weight-semi-bold)",
          }}
        >
          Faturas
        </h4>
        <div
          className="border border-border overflow-x-auto"
          style={{ borderRadius: "var(--radius)" }}
        >
          <table className="w-full" style={{ minWidth: 720 }}>
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-3 py-2">Nº</th>
                <th className="px-3 py-2" style={{ width: 100 }}>Vencimento</th>
                <th className="px-3 py-2 text-right" style={{ width: 110 }}>Total</th>
                <th className="px-3 py-2 text-right" style={{ width: 110 }}>Em aberto</th>
                <th className="px-3 py-2" style={{ width: 120 }}>Antiguidade</th>
                <th className="px-3 py-2" style={{ width: 130 }}>Estado</th>
                <th className="px-3 py-2" style={{ width: 130 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-secondary-text">
                    A carregar…
                  </td>
                </tr>
              ) : (account?.invoices.length ?? 0) === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-secondary-text">
                    Sem faturas registadas.
                  </td>
                </tr>
              ) : (
                account?.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {inv.number}
                      {inv.kind === "credit_note" && (
                        <span className="block text-xs text-secondary-text">
                          NC {inv.originalInvoiceNumber ? `· ${inv.originalInvoiceNumber}` : ""}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateShort(inv.dueDate)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{formatCurrency(inv.totalAmount)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {inv.openAmount > 0 ? formatCurrency(inv.openAmount) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {inv.openAmount > 0 ? (
                        <AgingBadge bucket={inv.agingBucket} ageDays={inv.ageDays} />
                      ) : (
                        <span className="text-secondary-text">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge code={inv.statusCode} name={inv.statusName} />
                    </td>
                    <td className="px-3 py-2">
                      {inv.isMigratedToAccount ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 bg-primary/10 text-primary"
                          style={{
                            fontSize: "var(--text-xs)",
                            borderRadius: "var(--radius)",
                            fontWeight: "var(--font-weight-medium)",
                          }}
                          title={
                            inv.migrationNotes ??
                            "Migrada para a conta corrente (JAL)"
                          }
                        >
                          Sob acordo
                        </span>
                      ) : isJalEligible(inv) ? (
                        <button
                          type="button"
                          className="text-primary underline disabled:opacity-50"
                          disabled={migratingId === inv.id}
                          onClick={() => openMigrate(inv)}
                        >
                          {migratingId === inv.id ? "A migrar…" : "Migrar (JAL)"}
                        </button>
                      ) : (
                        <span className="text-secondary-text">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Acordo de pagamento + processo jurídico */}
      <div className="bg-card border border-border p-6 space-y-4" style={cardStyle}>
        <h4
          className="text-foreground"
          style={{
            fontSize: "var(--text-section-title)",
            fontWeight: "var(--font-weight-semi-bold)",
          }}
        >
          Acordo de pagamento
        </h4>

        {form && (
          <>
            <datalist id={`conta-frequencia-${clientId}`}>
              {FREQUENCY_OPTIONS.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Valor da prestação (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.installmentAmount}
                  onChange={(event) => updateForm({ installmentAmount: event.target.value })}
                  placeholder="0"
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Limite de crédito (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.creditLimit}
                  onChange={(event) => updateForm({ creditLimit: event.target.value })}
                  placeholder="Sem limite"
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Frequência
                <input
                  type="text"
                  list={`conta-frequencia-${clientId}`}
                  value={form.frequency}
                  onChange={(event) => updateForm({ frequency: event.target.value })}
                  placeholder="Mensal / Trimestral…"
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Início do acordo
                <input
                  type="date"
                  value={form.agreementStartDate}
                  onChange={(event) => updateForm({ agreementStartDate: event.target.value })}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Fim do acordo
                <input
                  type="date"
                  value={form.agreementEndDate}
                  onChange={(event) => updateForm({ agreementEndDate: event.target.value })}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                Notas do acordo
                <textarea
                  rows={2}
                  value={form.agreementNotes}
                  onChange={(event) => updateForm({ agreementNotes: event.target.value })}
                  className={inputClass}
                />
              </label>
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={form.hasLegalProcess}
                  onChange={(event) => updateForm({ hasLegalProcess: event.target.checked })}
                />
                Processo jurídico em curso
              </label>
              {form.hasLegalProcess && (
                <label className="flex flex-col gap-1 text-sm text-secondary-text">
                  Notas do processo jurídico
                  <textarea
                    rows={2}
                    value={form.legalProcessNotes}
                    onChange={(event) => updateForm({ legalProcessNotes: event.target.value })}
                    className={inputClass}
                  />
                </label>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                disabled={isSaving}
                onClick={handleSave}
                className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isSaving ? "A guardar…" : "Guardar conta corrente"}
              </button>
              {savedMessage && (
                <span className="text-green-700 text-sm">{savedMessage}</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Documentos da conta corrente */}
      <EntityDocumentsSection
        ownerType="client"
        ownerId={clientId}
        section="client_account"
        title="Documentos da conta corrente"
        emptyLabel={`Sem documentos da conta corrente de ${clientName}.`}
      />

      {/* Migração JAL para conta corrente [3.6.6] */}
      {migrateInvoice && (
        <Modal
          title="Migrar fatura para conta corrente (JAL)"
          onClose={() => setMigrateInvoice(null)}
          maxWidthClass="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setMigrateInvoice(null)}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={migratingId !== null}
                onClick={confirmMigrate}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {migratingId !== null ? "A migrar…" : "Migrar"}
              </button>
            </>
          }
        >
          {migrateError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {migrateError}
            </div>
          )}
          <p className="text-sm text-secondary-text">
            A fatura {migrateInvoice.number} ({formatCurrency(migrateInvoice.openAmount)} em aberto)
            passa a "Sob acordo" e sai do radar de cobrança. A dívida mantém-se na conta corrente e
            na antiguidade de saldos.
          </p>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Condições / notas (opcional)
            <textarea
              rows={3}
              value={migrateNotes}
              onChange={(event) => setMigrateNotes(event.target.value)}
              placeholder="Ex.: plano de pagamento acordado em 6 prestações"
              className={inputClass}
            />
          </label>
        </Modal>
      )}
    </div>
  );
}
